import sessionService from "../../services/sessionService";

/* Session Timeline Calculation Functions */
const fillMissingDates = (data, dates) => {
  if (!dates.startDate || !dates.endDate) {
    console.warn(
      "Missing start or end date, returning empty list of session timeline entries.",
    );
    return [];
  }
  const allDates = [];
  for (
    let d = new Date(dates.startDate);
    d <= dates.endDate;
    d.setDate(d.getDate() + 1)
  ) {
    allDates.push(new Date(d));
  }
  const dataMap = new Map(data.map((item) => [item.date.toDateString(), item]));
  return allDates.map((date) => {
    const dateString = date.toDateString();
    return (
      dataMap.get(dateString) || {
        date: date,
        text: 0,
        voice: 0,
        total: 0,
      }
    );
  });
};

/**
 * Subtract the given number of days from the given time.
 * @param {Date} time
 * @param {int} days
 * @returns {Date}
 */
const timeMinusDays = (time, days) => {
  return new Date(time - days * 24 * 60 * 60 * 1000);
};

/**
 * Take the earlier of two dates.
 * @param {string} minOrMax
 * @param {Date} time1
 * @param {Date} time2
 * @returns {Date}
 */
const dateCompare = (minOrMax, time1, time2) => {
  if (minOrMax === "min") return time1 > time2 ? time2 : time1;
  return time1 > time2 ? time1 : time2;
};

/**
 * Fetches the timeline data for a given date and modality, filling in any missing dates with zero counts.
 * @param {Date} selectedDate - The currently selected date on which the timeline is centered.
 * @param {?Date} earliestDate - The date of the earliest available sessions.
 * @param {number} timelineRange - The number of days in the timeline.
 * @param {?string} modality - the name of the modality used to filter.
 * @param {?string} searchTerm - a search term that could match a wide variety of relevant fields.
 * @param {?{field: ?string, direction: ?string}} activeSort - the current sort selection.
 * @param {?string} status - the current display-status filter.
 * @returns {Promise<{bounds: {startDate: Date, endDate: Date, earliestDate: Date|null}, data: []|*[]}>}
 */
export const fetchTimeline = async (
  selectedDate,
  earliestDate,
  timelineRange,
  modality = null,
  searchTerm = null,
  activeSort = null,
  status = null,
) => {
  try {
    // Determine start and end dates.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let endTime = dateCompare(
      "min",
      today,
      timeMinusDays(selectedDate, -Math.floor(timelineRange / 2)),
    );
    let startTime = timeMinusDays(endTime, timelineRange);

    if (earliestDate) {
      startTime = dateCompare("max", startTime, earliestDate);

      if (endTime !== today) {
        endTime = timeMinusDays(startTime, -timelineRange);
      }
    }

    // Set the end date time to the end of the day
    endTime.setHours(23, 59, 59, 999);

    // Query the API
    const timelineData = await sessionService.getTimeline(
      startTime,
      endTime,
      modality,
      searchTerm,
      activeSort.field,
      status,
    );

    // Group the bounding date information.
    const timelineDates = {
      startDate: new Date(timelineData.start_date),
      endDate: new Date(timelineData.end_date),
      earliestDate: earliestDate,
    };

    // Make sure all the dates are set to the beginning of the day.
    Object.entries(timelineDates).forEach(([label, date]) => {
      date.setHours(0, 0, 0, 0);
    });

    // Make sure we have all the days.
    const data = fillMissingDates(
      timelineData.entries.map((entry) => ({
        ...entry,
        date: new Date(entry.date),
      })),
      timelineDates,
    );

    // Return our data structure.
    return { bounds: timelineDates, data: data };
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

/**
 * Get the header bounds based on the selected date.
 */
export const getHeaderBounds = async (
  selectedDate,
  activeSort,
  modalityFilter,
  searchTerm,
  sessionsPerPage,
  status = null,
) => {
  const orderBy = activeSort.field
    ? `${activeSort.field}:${activeSort.direction}`
    : "created_at:desc";
  const start = new Date(selectedDate);
  start.setHours(0, 0, 0, 0);
  const end = new Date(selectedDate);
  end.setHours(23, 59, 59, 999);
  return await sessionService.getHeaderPageBounds(
    start,
    end,
    modalityFilter,
    searchTerm,
    orderBy,
    sessionsPerPage,
    status,
  );
};

/* TabbedView Content Generation Functions */
export const getSessionProperties = (
  session,
  showType = "basic",
  events = [],
  expandedProperties = {},
  togglePropertyExpansion = () => {},
) => {
  if (!session) return null;

  let durationString = "N/A";
  if (session.created_at && session.ended_at) {
    const start = new Date(session.created_at);
    const end = new Date(session.ended_at);
    const durationMs = end - start;
    const seconds = Math.floor(durationMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const paddedHours = hours.toString().padStart(2, "0");
    const paddedMinutes = (minutes % 60).toString().padStart(2, "0");
    const paddedSeconds = (seconds % 60).toString().padStart(2, "0");
    durationString = `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
  }

  const properties = JSON.parse(JSON.stringify(session));
  properties.duration = durationString;
  properties.event_count = events.length;
  properties.message_count = events.filter(
    (f) =>
      (f?.actor === "generative" && f?.event === "completed") ||
      (f?.actor === "stt" && f?.event === "transcription") ||
      (f?.actor === "user" && f?.event === "message") ||
      (f?.actor === "assistant" && f?.event === "message"),
  ).length;
  properties.median_internal_latency =
    properties.voice_statistic?.median_internal_latency?.toFixed(3);
  properties.min_internal_latency =
    properties.voice_statistic?.min_internal_latency?.toFixed(3);
  properties.max_internal_latency =
    properties.voice_statistic?.max_internal_latency?.toFixed(3);

  const propNames = [
    "id",
    "created_at",
    "to_phone_number",
    "modality",
    "duration",
    "event_count",
    "message_count",
    "median_internal_latency",
    "min_internal_latency",
    "max_internal_latency",
  ];

  const filteredEntries = Object.entries(properties).filter(([key]) => {
    if (showType === "basic") {
      return propNames.includes(key);
    } else {
      return !propNames.includes(key);
    }
  });

  const shouldTruncate = (value) => {
    if (
      typeof value === "object" &&
      value !== null &&
      Object.keys(value).length !== 0
    )
      return true;
    return String(value || "").length > 50;
  };

  const formatValue = (key, value, isExpanded) => {
    if (value === null || value === undefined) return "-";
    if (typeof value === "object") {
      return JSON.stringify(value, null, isExpanded ? 2 : 0);
    }
    const stringValue = String(value);
    if (stringValue.length > 50 && !isExpanded) {
      return `${stringValue.substring(0, 50)}...`;
    }
    return stringValue;
  };

  return (
    <div className="px-4 py-2 space-y-1">
      {filteredEntries.map(([key, value], index) => {
        const isExpanded = expandedProperties[key] || false;
        const canExpand = shouldTruncate(value);

        return (
          <div key={key} className="group">
            <div className="flex justify-between items-start py-2">
              <div className="font-medium text-gray-300 capitalize w-1/3 text-left pt-1">
                {key.replaceAll("_", " ")}
              </div>
              <div className="text-white w-2/3 text-right break-words">
                <div
                  className={`${
                    isExpanded ? "whitespace-pre-wrap text-left" : "truncate"
                  }`}
                >
                  {formatValue(key, value, isExpanded)}
                </div>
                {canExpand && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      togglePropertyExpansion(key);
                    }}
                    className="text-xs text-blue-400 hover:text-blue-300 mt-1"
                  >
                    {isExpanded ? "Show less" : "Show more"}
                  </button>
                )}
              </div>
            </div>
            {index < filteredEntries.length - 1 && (
              <div className="border-b border-gray-700 my-1"></div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// used to download the transcript for sessions
export const handleDownloadTranscript = async ({ sessionId }) => {
  try {
    const transcriptResponse = await sessionService.getTranscript(sessionId);

    if (transcriptResponse && transcriptResponse.transcript) {
      const jsonString = JSON.stringify(transcriptResponse.transcript, null, 2);
      const blob = new Blob([jsonString], {
        type: "application/json;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `transcript-${sessionId}.json`);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } else {
      console.log("No transcript data found.");
    }
  } catch (error) {
    console.error("Error downloading transcript JSON:", error);
  }
};

export const handleDownloadDiagnostics = async ({ sessionId }) => {
  try {
    const response = await sessionService.getDiagnostics(sessionId);
    const blob = response?.data;
    if (!blob) {
      throw new Error("No diagnostics found.");
    }
    const status = response?.status;
    const contentType = (
      response?.headers?.["content-type"] || ""
    ).toLowerCase();
    if (status !== 200 || !contentType.includes("application/zip")) {
      const errorBody =
        typeof blob.text === "function" ? await blob.text() : String(blob);
      console.error("Diagnostics download failed", {
        sessionId,
        status,
        contentType,
        errorBody,
      });
      throw new Error("Diagnostics download failed.");
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const contentDisposition = response.headers?.["content-disposition"] || "";
    const filenameMatch = contentDisposition.match(/filename="([^"]+)"/i);
    const filename =
      filenameMatch?.[1] || `${sessionId}_voice_bot_diagnostics.zip`;
    link.setAttribute("download", filename);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading diagnostics zip:", error);
    throw error;
  }
};
